import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, AlertTriangle, XCircle, Clock } from 'lucide-react';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { Drawer } from '@heyroot/ui/primitives/Drawer';
import { Textarea } from '@heyroot/ui/primitives/Textarea';
import { authenticatedFetch } from '../../lib/authenticated-fetch';
import type { AccountingModule } from '../../pages/accounting-modules';
import {
  ACCOUNTING_MODULE_LABEL,
  ACCOUNTING_MODULE_DESCRIPTION,
  ACCOUNTING_MODULE_GL_ENTRIES,
  ACCOUNTING_MODULE_TIER_LABEL,
} from '../../lib/accounting-module-metadata';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ActivationResult {
  status: 'activated' | 'already_active' | 'dependency_blocked' | 'conflict' | 'invalid_transition';
  blockedBy?: string;
}

interface SequenceModuleResult {
  moduleKey: string;
  result: ActivationResult;
}

interface SequenceResponse {
  results: SequenceModuleResult[];
}

// ─── API ──────────────────────────────────────────────────────────────────────

async function postActivateSequence(
  tenantId: string,
  body: { reason: string; requestId: string },
): Promise<SequenceResponse> {
  const res = await authenticatedFetch(
    `/api/tenants/${encodeURIComponent(tenantId)}/accounting-modules/activate-sequence`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    let detail = '';
    try {
      const b = (await res.json()) as { message?: string };
      detail = b.message ?? '';
    } catch {
      // non-JSON
    }
    throw new Error(detail || `Request failed: ${res.status}`);
  }
  return res.json() as Promise<SequenceResponse>;
}

// ─── Result icon ─────────────────────────────────────────────────────────────

type ResultStatus = ActivationResult['status'] | 'pending';

function ResultIcon({ status }: { status: ResultStatus }) {
  if (status === 'pending') return <Clock className="h-4 w-4 text-muted" aria-label="Pending" />;
  if (status === 'activated')
    return <CheckCircle2 className="h-4 w-4 text-success-text" aria-label="Activated" />;
  if (status === 'already_active')
    return <CheckCircle2 className="h-4 w-4 text-muted" aria-label="Already active" />;
  if (status === 'dependency_blocked')
    return <AlertTriangle className="h-4 w-4 text-warning-text" aria-label="Blocked" />;
  return <XCircle className="h-4 w-4 text-danger-text" aria-label="Failed" />;
}

function resultLabel(r: ActivationResult): string {
  switch (r.status) {
    case 'activated':
      return 'Activated';
    case 'already_active':
      return 'Already active';
    case 'dependency_blocked':
      return r.blockedBy
        ? `Blocked — needs ${ACCOUNTING_MODULE_LABEL[r.blockedBy] ?? r.blockedBy}`
        : 'Blocked';
    case 'conflict':
      return 'Conflict — module in transition';
    case 'invalid_transition':
      return 'Invalid state transition';
  }
}

function resultTextClass(status: ActivationResult['status']): string {
  if (status === 'activated') return 'text-success-text';
  if (status === 'already_active') return 'text-muted';
  return 'text-warning-text';
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface GuidedActivationDrawerProps {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  modules: AccountingModule[];
  onSuccess: () => void;
}

export function GuidedActivationDrawer({
  open,
  onClose,
  tenantId,
  modules,
  onSuccess,
}: GuidedActivationDrawerProps) {
  const [reason, setReason] = useState('');
  const [results, setResults] = useState<SequenceModuleResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Stable requestId per submit attempt — regenerated on drawer open so retries
  // within the same attempt are idempotent; a new open starts a fresh attempt.
  const requestIdRef = useRef<string>(crypto.randomUUID());

  const inactiveModules = modules
    .filter((m) => m.status === 'inactive' || m.status === 'failed')
    .sort((a, b) => a.tier - b.tier);

  const mutation = useMutation({
    mutationFn: () => postActivateSequence(tenantId, { reason, requestId: requestIdRef.current }),
    onSuccess: (data) => {
      setResults(data.results);
      onSuccess();
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : 'Activation failed.');
    },
  });

  function handleClose() {
    setReason('');
    setResults(null);
    setError(null);
    requestIdRef.current = crypto.randomUUID(); // fresh ID for next open
    onClose();
  }

  // Group inactive modules by tier for display
  const tierGroups = new Map<number, AccountingModule[]>();
  for (const mod of inactiveModules) {
    const arr = tierGroups.get(mod.tier) ?? [];
    arr.push(mod);
    tierGroups.set(mod.tier, arr);
  }

  const resultMap = new Map<string, ActivationResult>(results?.map((r) => [r.moduleKey, r.result]));

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      placement="right"
      width="pos"
      title="Guided Activation"
    >
      <div className="flex flex-col gap-6 overflow-y-auto">
        {/* Intro */}
        <p className="text-body-sm text-secondary">
          Activate all inactive GL modules in dependency order. Modules already active are skipped.
          Modules blocked by an unmet dependency will report their blocker.
        </p>

        {/* Module list */}
        {inactiveModules.length === 0 ? (
          <div className="rounded-control border border-success/30 bg-success-pale text-success-text p-4 text-body-sm">
            All accounting modules are already active.
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {Array.from(tierGroups.entries()).map(([tier, mods]) => (
              <div key={tier} className="flex flex-col gap-2">
                <h3 className="text-eyebrow uppercase text-muted">
                  Tier {tier} — {ACCOUNTING_MODULE_TIER_LABEL[tier]}
                </h3>
                {mods.map((mod) => {
                  const res = resultMap.get(mod.moduleKey);
                  return (
                    <div
                      key={mod.moduleKey}
                      className="rounded-control border border-border bg-surface-deep p-3 flex items-start gap-3"
                    >
                      <div className="mt-0.5 shrink-0">
                        <ResultIcon status={res ? res.status : 'pending'} />
                      </div>
                      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-body-sm-emphasis text-primary">
                            {ACCOUNTING_MODULE_LABEL[mod.moduleKey] ?? mod.moduleKey}
                          </span>
                          {mod.status === 'failed' && (
                            <Badge variant="danger" size="sm">
                              Failed
                            </Badge>
                          )}
                        </div>
                        <p className="text-caption text-muted">
                          {ACCOUNTING_MODULE_DESCRIPTION[mod.moduleKey]}
                        </p>
                        <p className="text-caption text-muted/70 font-mono">
                          {ACCOUNTING_MODULE_GL_ENTRIES[mod.moduleKey]}
                        </p>
                        {res && (
                          <p
                            className={`text-body-sm-emphasis leading-4 ${resultTextClass(res.status)}`}
                          >
                            {resultLabel(res)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {/* Reason + action */}
        {inactiveModules.length > 0 && !results && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="guided-reason" className="text-eyebrow uppercase text-muted">
                Reason for activation <span className="text-danger-text">*</span>
              </label>
              <Textarea
                id="guided-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Initial accounting activation for tenant onboarding…"
                rows={3}
                disabled={mutation.isPending}
              />
            </div>
            {error && (
              <p role="alert" className="text-body-sm text-danger-text">
                {error}
              </p>
            )}
            <Button
              variant="primary"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || reason.trim().length === 0}
            >
              {mutation.isPending
                ? 'Activating…'
                : `Activate ${inactiveModules.length} module${inactiveModules.length === 1 ? '' : 's'}`}
            </Button>
          </div>
        )}

        {/* Post-run summary */}
        {results &&
          (() => {
            const activatedCount = results.filter((r) => r.result.status === 'activated').length;
            const needsFollowUp = results.filter(
              (r) =>
                r.result.status === 'conflict' ||
                r.result.status === 'dependency_blocked' ||
                r.result.status === 'invalid_transition',
            ).length;
            return (
              <div className="flex flex-col gap-3">
                <p className="text-body-sm text-muted">
                  Activated <strong className="text-success-text">{activatedCount}</strong> of{' '}
                  {results.length} modules.
                </p>
                {needsFollowUp > 0 && (
                  <p role="alert" className="text-body-sm text-warning-text">
                    {needsFollowUp} module{needsFollowUp === 1 ? '' : 's'} could not be activated —
                    check the details above.
                  </p>
                )}
                <Button variant="ghost" onClick={handleClose}>
                  Done
                </Button>
              </div>
            );
          })()}
      </div>
    </Drawer>
  );
}
